import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { getTimedMetadata, updateTimedMetadata } from '@mimir/web/util/api';
import type {
  TimedMetadataItem,
  UpdateTimedMetadataRequestDto,
} from '@mimir/common/dto/timedMetadataModels';
import type { TimedMdfUsageRule } from '@mimir/common/dto/timedMdfUsage';
import { getRestrictedFormUsageRules } from '@mimir/web/util/usageRestrictionsCheck';
import { getUsageRuleFromTimedMdf } from '@mimir/common/timed-mdf/usage-rules';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import { createUuid } from '@mimir/common/util/createUuid';
import type { Dispatch, Module } from 'vuex';
import type { State } from './state';
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import { showToast, TOAST } from '@mimir/web/eventBus';
import Vue from 'vue';
import type { Item } from '@mimir/common/dto/searchResultHit';
import { applyIngestTimeIfUsageRuleExists, getIngestTimeFromItem } from '@mimir/common/util/timedMetadata';
import TimelineItem from '@mimir/web/model/timeline/TimelineItem';
import type { TimedMdfModuleState, TimedMetadataItemModel } from './timed-mdf-module-state';
import type { AxiosError } from 'axios';
import { abortable, isAbortError } from '@mimir/common/util/abortControl';
import type { ItemMediaPlayerOps } from '@mimir/web/model/itemMediaPlayerOps';
import type Timeline from '@mimir/client-common/timeline/Timeline';


const timedMdfState: TimedMdfModuleState = {
  timedMdfSaving: false,
  selectedItem: null,
  selectedTimedMdf: null,
  hoveredItem: null,
  itemsWithChanges: [],
  loadingCollection: false,
  loadedCollection: false,
  itemMdfCollection: {
    items: [],
    eventsSnapshot: [],
  },
  shouldAutoFocusSelectedItem: true
};

export const ITEM_HEIGHT = 10;

export const timedMdfModule: Module<TimedMdfModuleState, State> = {
  namespaced: true,
  state: timedMdfState,
  mutations: {
    loadedCollection: (state, payload: boolean) => state.loadedCollection = payload,
    loadingCollection: (state, payload: boolean) => state.loadingCollection = payload,
    itemHasChanged: (state, payload: { timedItemId: string, hasChanged: boolean }) => updateItemChanged(state, payload),
    clearChangedItemsState: (state) => state.itemsWithChanges = [],
    selectedTimedMdf: (state, payload: MetaDataSchema | null) => state.selectedTimedMdf = payload,
    setIsSaving: (state, payload: boolean) => state.timedMdfSaving = payload,
    setMdfCollection: (state, payload: TimedMetadataItemModel[]) => {
      state.itemMdfCollection.items = payload;
      state.itemMdfCollection.eventsSnapshot = cloneDeep(payload);
    },
    add: (state, payload: TimedMetadataItemModel) => {
      state.itemMdfCollection.items.push(payload);
      if (payload.isNewNonSavedItem) {
        state.itemMdfCollection.eventsSnapshot.push(payload);
      }
    },
    setSelectedItemHasErrors: (state, payload: boolean) => {
      if (state.selectedItem) {
        state.selectedItem.hasValidationErrors = payload;
      }
    },
    remove: (state, timedItem: TimedMetadataItemModel) => {
      removeTimedMdf(state, timedItem.id);
      // If the removed item has only been added client side from the UI
      // we just remove it now, otherwise we must make sure to later
      // send a delete for it when saving.
      const requiresSave = !timedItem.isNewNonSavedItem;
      updateItemChanged(state, {timedItemId: timedItem.id, hasChanged: requiresSave});
      if (state.selectedItem?.id === timedItem.id) {
        state.selectedItem = null;
      }
    },
    setSelected: (state, payload: TimedMetadataItemModel | null) => state.selectedItem = payload,
    setShouldAutoFocusSelectedItem: (state, payload: boolean) => state.shouldAutoFocusSelectedItem = payload,
    setHovered: (state, payload: TimedMetadataItemModel | null) => state.hoveredItem = payload,
    setTimedMdfSnapshot: (state, payload: TimedMetadataItemModel[]) => state.itemMdfCollection.eventsSnapshot = payload,
  },
  getters: {
    collectionIsLoaded: state => state.loadedCollection,
    collectionIsLoading: state => state.loadingCollection,
    itemsWithChanges: state => state.itemsWithChanges,
    selectedTimedMdf: state => state.selectedTimedMdf,
    shouldAutoFocusSelectedItem: state => state.shouldAutoFocusSelectedItem,
    timedMdfSaving: state => state.timedMdfSaving,
    itemMdfCollectionSorted: state => sortBy(state.itemMdfCollection.items, [item => item.startMs]),
    itemMdfCollection: state => state.itemMdfCollection.items,
    selectedItem: state => state.selectedItem,
    hoveredItem: state => state.hoveredItem,
    usageRules: (_, __, ___, rootGetters) => {
      const mdfs = (rootGetters.timedMdfs ?? []) as MetaDataSchema[];
      return getRestrictedFormUsageRules(mdfs);
    },
  },
  actions: {
    updateItemsWithSnapShot: (context) => {
      const itemsWithChanges = context.state.itemsWithChanges;
      const snapshot = context.state.itemMdfCollection.eventsSnapshot;
      const items = context.state.itemMdfCollection.items;

      const selectedItem = context.state.selectedItem;
      if (selectedItem?.isNewNonSavedItem) context.commit('remove', selectedItem);

      itemsWithChanges.forEach(id => {
        const snapshotItem = snapshot.find(item => item.id === id);
        if (snapshotItem) {
          const itemToUpdate = items.find(item => item.id === id);
          if (itemToUpdate) {
            Object.assign(itemToUpdate, snapshotItem);
          }
        }
      });
      context.commit('setSelected', null);
      context.commit('clearChangedItemsState');
    },
    addSelectedMdf: async (context, timecode?: string) => {
      if (!context.state.loadedCollection) {
        await context.dispatch('loadTimedMdf');
      }

      if (context.state.selectedTimedMdf !== null) {
        return context.dispatch('add', { mdf: context.state.selectedTimedMdf, timecode });
      }
    },
    add: (context, info: { mdf: MetaDataSchema, timecode?: string, playerOps: ItemMediaPlayerOps }) => {
      const timeline = context.rootGetters.timeline as Timeline;
      let timeRange = timeline.getTimeRange(
        info.playerOps.getDurationInMsObs().currentValue(),
        info.playerOps.getVirtualClipStartObs().currentValue(),
      );
      let setStart = true;

      // If no selected time range is available, we set the start time to the current time and use the duration of the mdf, or the default 5 seconds.
      if (timeRange === null) {
        const currentTime = info.playerOps.getVirtualClipMediaPositionObs().currentValue().approximate * 1000.0;
        const videoDuration = info.playerOps.getDurationInMsObs().currentValue();
        const logEventDuration = (info.mdf.duration ?? 5) * 1000;

        let start = currentTime;
        let end = Math.min(currentTime + logEventDuration, videoDuration);
        const minLogEventDuration = 1000; // 1 second

        // If the calculated end time equals the current time (zero duration),
        // adjust start time backwards to maintain desired duration
        if ((end - minLogEventDuration) <= currentTime) {
          start = Math.max(0, videoDuration - minLogEventDuration);
          end = videoDuration;
        }

        timeRange = { start, end };
        setStart = false;
      } else {
        const end = Math.max(timeRange.start + TimelineItem.MIN_DURATION, timeRange.end);
        timeRange = {
          start: timeRange.start,
          end
        };
      }

      const payload: ItemMetaData = {
        formId: info.mdf.id,
        formData: {}
      };
      const errors = info.mdf.sanitize(context.rootGetters.optionLists, payload, false);
      const newTimedMdfItem: TimedMetadataItemModel = {
        id: createUuid(),
        swimlane: 0,
        hasValidationErrors: !isEmpty(errors),
        isNewNonSavedItem: true,
        startMs: timeRange.start,
        endMs: timeRange.end,
        data: payload,
        timecode: info.timecode,
        selected: true,

        // If this timed item is saved the server code will set the creationInfo
        // based on the received credentials. But we still need to set it here to
        // mark this item as having been created by the current user.
        creationInfo: {
          source: 'api',
          date: new Date().toISOString(),
          userId: context.rootGetters.user.id,
        }
      };
      context.commit('add', newTimedMdfItem);
      context.commit('itemHasChanged', {timedItemId: newTimedMdfItem.id, hasChanged: true});
      context.commit('clearTimelineState', undefined, {root: true});
      return setSelectedTimedMdfItemLocal(context.dispatch, { playerOps: info.playerOps, item: newTimedMdfItem, setStart, shouldAutoFocusSelectedItem: true });
    },
    copyTimedMdf: (context, payload: { oldTimedMdfItem: TimedMetadataItem, playerOps: ItemMediaPlayerOps }) => {
      const { oldTimedMdfItem, playerOps } = payload;
      const currentTime = playerOps.getVirtualClipMediaPositionObs().currentValue().approximate * 1000.0;
      const duration = playerOps.getDurationInMsObs().currentValue();
      let endMs = (oldTimedMdfItem.endMs - oldTimedMdfItem.startMs) + currentTime;
      if (endMs > duration) endMs = duration;

      const timedMdf = context.rootGetters.timedMdfs?.filter((mdf: MetaDataSchema) => mdf.id === oldTimedMdfItem.data.formId)[0] as MetaDataSchema;
      const errors = timedMdf.sanitize(context.rootGetters.optionLists, oldTimedMdfItem.data, false);

      const newTimedMdfItem: TimedMetadataItemModel = {
        ...oldTimedMdfItem,
        startMs : currentTime,
        endMs,
        id: createUuid(),
        swimlane: 0,
        hasValidationErrors: !isEmpty(errors),
        isNewNonSavedItem: true,
        creationInfo: {
          source: 'api',
          date: new Date().toISOString(),
          userId: context.rootGetters.user.id,
        }
      };
      context.commit('add', newTimedMdfItem);
      context.commit('itemHasChanged', {timedItemId: newTimedMdfItem.id, hasChanged: true});
      context.commit('clearTimelineState', undefined, {root: true});
      context.commit('clearTimelineState', undefined, {root: true});
      return setSelectedTimedMdfItemLocal(
        context.dispatch,
        {
          playerOps,
          item: newTimedMdfItem,
          setStart: true,
          // Set auto-focus to false so that user can keep pasting multiple times without interruption
          shouldAutoFocusSelectedItem: false
        }
      );
    },
    saveItems: async (context, { timedItemIds, abortController }: SaveTimedMdfItemsPayload) => {
      context.commit('setIsSaving', true);
      try {
        const itemsById = keyBy(context.state.itemMdfCollection.items, it => it.id);
        const update: UpdateTimedMetadataRequestDto['items'] = {};
        for (const itemId of timedItemIds) {
          const item = itemsById[itemId];
          if (item?.hasValidationErrors) {
            // We avoid saving an item with validation errors,
            // as we know it will fail the request.
            continue;
          }
          // For each item to save, we either update with the current value
          // or delete it if the item has been removed from the UI.
          update[itemId] = item ? modelToDto(item) : null;
        }
        if (isEmpty(update)) {
          return; // nothing to save
        }
        const request: UpdateTimedMetadataRequestDto = {
          items: update
        };
        const currentItem = context.rootState.timeline.currentItem as Item | undefined;
        if (!currentItem) throw new Error('Expected current item to be present');

        await abortable(
          async () => {
            await updateTimedMetadata(currentItem.id, request);
          },
          abortController
        );

        const mdfs = context.rootGetters.timedMdfs as MetaDataSchema[];
        const usageRules = getRestrictedFormUsageRules(mdfs);
        timedItemIds.forEach(timedItemId => {
          context.commit('itemHasChanged', { timedItemId, hasChanged: false });
          const item = itemsById[timedItemId];
          if (item) {
            item.isNewNonSavedItem = false;
            if (usageRules) {
              Vue.set(item, 'usageRule', getUsageRuleFromTimedMdf(item, usageRules));

              // Inject ingest time in local items as it should be set in the update api
              applyIngestTimeIfUsageRuleExists(item, getIngestTimeFromItem(currentItem));
            }
          }
        });
        context.commit('setTimedMdfSnapshot', cloneDeep(context.state.itemMdfCollection.items));
        showToast('Log events updated', TOAST.SUCCESS);
      } catch (err) {
        if (isAbortError(err)) throw err;

        if ((err as AxiosError).response?.status === 400) logErrorAndShowToast('Please fix all validation errors in all log events', err, 'ex in saveItems');
        else logErrorAndShowToast('Failed updating log events, please try again later', err, 'ex in saveItems');
      } finally {
        context.commit('setIsSaving', false);
      }
    },
    loadTimedMdf: async (context) => {
      // Sometimes multiple UI components may request a load due to several possible codepaths of triggering
      // a load. For now the following different user interactions can lead to the timed MDF collection loaded:
      // 1) User presses a or alt + a on the timeline to add a timed mdf item
      // 2) User pressed on the log events button in the player controls row
      // 3) User uncollapsed the Logging section in the metadata tab.
      // To ensure we don't fire double loads, we cancel any dispatches while we are already loading.
      const loadingOrLoaded = context.state.loadingCollection || context.state.loadedCollection;
      if (loadingOrLoaded) {
        return;
      }

      const currentItemPageItem = context.rootState.timeline.currentItem;
      if (currentItemPageItem === null)
        return;

      context.commit('loadingCollection', true);
      try {
        const collection = await getTimedMetadata(currentItemPageItem.id);
        const mdfs = context.rootGetters.timedMdfs as MetaDataSchema[];
        const usageRules = getRestrictedFormUsageRules(mdfs);
        const items = collection.items.map(it => dtoToModel(it, usageRules, currentItemPageItem.timecode));
        context.commit('setMdfCollection', items);
        context.commit('setSelected', null);
        // Default selected mdf to the first one in the collection
        if (mdfs.length > 0) {
          context.commit('selectedTimedMdf', mdfs[0]);
        }
        context.commit('loadedCollection', true);
      } catch (err) {
        logErrorAndShowToast('Failed loading log events, please try again later', err, 'ex in loadTimedMdf');
      } finally {
        context.commit('loadingCollection', false);
      }
    },
    setSelectedItem: (context, data: SetSelectedTimedMdfItemPayload) => {
      context.commit('setSelected', data.item);
      context.commit('setShouldAutoFocusSelectedItem', data.shouldAutoFocusSelectedItem);
      if (data.item !== null && data.setStart) {
        data.playerOps.seekToVirtualClipMediaPosition({ approximate: data.item.startMs / 1000.0 });
      }
    },
    timedMdfModuleCleanup: context => {
      context.commit('setSelected', null);
      context.commit('setMdfCollection', []);
      context.commit('setIsSaving', false);
      context.commit('clearChangedItemsState');
      context.commit('loadingCollection', false);
      context.commit('loadedCollection', false);
    }
  }
};

const removeTimedMdf = (state: TimedMdfModuleState, timedItemId: string) => {
  let itemRemovedFromCollection = false;

  for (let i = 0; i < state.itemMdfCollection.items.length; i++) {
    if (state.itemMdfCollection.items[i].id === timedItemId) {
      state.itemMdfCollection.items.splice(i, 1);
      itemRemovedFromCollection = true;
    }

    if (state.itemMdfCollection.eventsSnapshot[i] && state.itemMdfCollection.eventsSnapshot[i].id === timedItemId) {
      state.itemMdfCollection.eventsSnapshot.splice(i, 1);
    }

    if (itemRemovedFromCollection) {
      break;
    }
  }
};

const updateItemChanged = (state: TimedMdfModuleState, payload: { timedItemId: string, hasChanged: boolean }) => {
  const {timedItemId, hasChanged} = payload;
  const withChanges = state.itemsWithChanges;
  if (!hasChanged) {
    state.itemsWithChanges = withChanges.filter(id => id !== timedItemId);
  } else {
    const alreadyIn = withChanges.includes(timedItemId);
    if (!alreadyIn) {
      withChanges.push(timedItemId);
    }
  }
};

function modelToDto(model: TimedMetadataItemModel): TimedMetadataItem {
  return {
    id: model.id,
    data: cloneDeep(model.data),
    startMs: model.startMs,
    endMs: model.endMs,
  };
}

export function dtoToModel(dto: TimedMetadataItem, usageRules?: TimedMdfUsageRule[], timecode?: string): TimedMetadataItemModel {
  return {
    hasValidationErrors: false,
    isNewNonSavedItem: false,
    swimlane: 0,
    timecode,
    ...cloneDeep(dto),
    ...(usageRules && { usageRule: getUsageRuleFromTimedMdf(dto, usageRules) })
  };
}

/**
 * Payload sent to the 'setSelectedItem' Vuex action.
 */
export interface SetSelectedTimedMdfItemPayload {
  item: TimedMetadataItem | null;
  /**
   * Whether the timeline should be set to the event's start time
   */
  setStart: boolean;
  /**
   * Whether the first text field in the timed MDF form should be focused automatically
   */
  shouldAutoFocusSelectedItem: boolean;

  playerOps: ItemMediaPlayerOps;
}

/**
 * Type safe wrapper around the 'setSelectedItem' action.
 * NOTE: Use only when calling it from within the timed-mdf-module itself, i.e. locally.
 */
const setSelectedTimedMdfItemLocal = (dispatch: Dispatch, payload: SetSelectedTimedMdfItemPayload) => {
  return dispatch('setSelectedItem', payload);
};

/**
 * Type safe wrapper around the 'setSelectedItem' action.
 * Meant to be called from components/code outside the timed-mdf-module
 */
export const setSelectedTimedMdfItem = (dispatch: Dispatch, payload: SetSelectedTimedMdfItemPayload) => {
  return dispatch('timedMdf/setSelectedItem', payload);
};

interface SaveTimedMdfItemsPayload {
  timedItemIds: string[];
  abortController: AbortController;
}

/**
 * Type safe wrapper around the 'saveItems' action.
 * Meant to be called from components/code outside the timed-mdf-module
 */
export async function saveTimedMdfItems(dispatch: Dispatch, payload: SaveTimedMdfItemsPayload) {
  return await dispatch('timedMdf/saveItems', payload);
}
